import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

import Button from '$partials/ui/button';
import { Image } from '$partials/media';
import IconPlus from '$partials/icons/plus';
import IconClose from '$partials/icons/close';
import RichText from '$partials/global/rich-text';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */

const ModuleModal = ({
    bodyRichText,
    buttonColour,
    eyebrowTitle,
    eyebrowIcon,
    images,
    title
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const dialogRef = useRef();

    useEffect(() => {
        const dialogNode = dialogRef.current;
        if (isModalOpen) {
            if (dialogNode && !dialogNode.open) {
                disableBodyScroll(dialogRef.current);
                dialogNode.showModal?.();
            }
        } else {
            if (dialogNode && dialogNode.open) {
                clearAllBodyScrollLocks();
                dialogNode.close?.();
            }
        }
        return () => {
            disableBodyScroll(dialogRef.current);
        };
    }, [isModalOpen]);

    const buttonBgColour = buttonColour || 'bg-sand';

    return (
        <>

            <dialog
                className='dialog-reset'
                ref={dialogRef}>
                <div className='layout-container'>
                    <div className='px-10 sm:px-5 md:px-10 flex justify-center py-60 sm:py-40 md:pt-85 md:pb-80'>
                        <div className='rounded flex flex-col w-full md:w-10/12 bg-white relative'>
                            <div className='absolute top-0 right-0 mt-30 mr-30 sm:mt-40 sm:mr-40'>
                                <Button
                                    aria-label='Close modal'
                                    as='button'
                                    className='p-13 bg-sand rounded-button pointer-events-auto'
                                    onClick={() => setIsModalOpen(false)}
                                    renderIcon={<IconClose/>} />
                            </div>
                            <div className='w-full grid grid-cols-12 md:grid-cols-10 gap-x-20 mt-[92px] sm:mt-[102px] md:mt-[124px] sm:mb-35 md:mb-80'>
                                <div className='col-span-1 sm:col-span-2'/>
                                <div className='col-span-10 sm:col-span-8 md:col-span-6'>
                                    {eyebrowTitle && (
                                        <div className='flex flex-row items-center mb-8 sm:mb-20'>
                                            {eyebrowIcon && (
                                                <div
                                                    className='mr-10'
                                                    style={{
                                                        fontSize: '18px',
                                                        width: `${(eyebrowIcon?.width / eyebrowIcon?.height)}em` ,
                                                        maxWidth: '100%'
                                                    }}>
                                                    <Image
                                                        {...eyebrowIcon}
                                                        loading={''}/>
                                                </div>
                                            )}
                                            <p className='type-style-7 text-orange'>
                                                {eyebrowTitle}
                                            </p>
                                        </div>
                                    )}
                                    <p className='type-style-3'>
                                        {title}
                                    </p>
                                    {bodyRichText && (
                                        <RichText
                                            className='rich-text mt-5 sm:mt-20 type-style-8'
                                            {...bodyRichText} />
                                    )}
                                </div>
                            </div>
                            {images?.items?.length > 0 && (
                                images.items.map((image, id) => {
                                    return (
                                        <div key={id}>
                                            <Image
                                                {...image}
                                                style={{width: undefined}} />
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </dialog>

            <Button
                aria-label='Open modal'
                as='button'
                className={`p-13 rounded-button ${buttonBgColour}`}
                onClick={() => setIsModalOpen(true)}
                renderIcon={<IconPlus/>} />
        </>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

ModuleModal.propTypes = {
    bodyRichText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    buttonColour: PropTypes.string,
    eyebrowIcon: PropTypes.object,
    eyebrowTitle: PropTypes.string,
    title: PropTypes.string,
    images: PropTypes.object,
};

export default ModuleModal;
